import { isDefined } from "@clipboard-health/util-ts";
import { type RushFeeConfig } from "@src/appV2/Auth/types";
import { getServerNow } from "@src/appV2/lib/clockOffset";
import { formatChargeDifferentialPercent } from "@src/appV2/redesign/WorkplaceChargeLeadTimeDifferentials/constants";
import { differenceInMinutes } from "date-fns";

/**
 * Narrowed legacy rush-fee config: both fields are present numbers. The wire shape
 * (`RushFeeConfig`) has nullish inner fields per the Zod contract; callers should
 * collapse via `narrowRushFeeConfig` once at the session boundary.
 */
export interface LegacyRushFee {
  differential: number;
  period: number;
}

/**
 * Collapses the wire-shaped `RushFeeConfig | null | undefined` into the narrow
 * `LegacyRushFee | undefined` once at the session boundary, so downstream code
 * can operate on plain `number` fields without nullish guards.
 */
export function narrowRushFeeConfig(
  raw: RushFeeConfig | null | undefined
): LegacyRushFee | undefined {
  if (!isDefined(raw)) {
    return undefined;
  }
  const { differential, period } = raw;
  return isDefined(differential) && isDefined(period) ? { differential, period } : undefined;
}

export interface RushFeeTier {
  id: string;
  thresholdInHours: number;
  incrementalMultiplier: number;
}

interface RawLeadTimeDifferential {
  id: string;
  attributes: { thresholdInHours: number; incrementalMultiplier: number };
}

/**
 * Normalize the API response into the `RushFeeTier` domain shape, sorted ascending
 * by threshold so callers can pass it straight to `findApplicableRushFeeTier`.
 */
export function mapToRushFeeTiers(
  differentials: readonly RawLeadTimeDifferential[] | undefined
): RushFeeTier[] {
  return (differentials ?? [])
    .map((differential) => ({
      id: differential.id,
      thresholdInHours: differential.attributes.thresholdInHours,
      incrementalMultiplier: differential.attributes.incrementalMultiplier,
    }))
    .sort((a, b) => a.thresholdInHours - b.thresholdInHours);
}

/**
 * UI-shaped, pre-formatted view of an applicable rush fee. The `amount` is rendered
 * differently per source (tier → "+10%", legacy → "$X.XX/hour") but the surrounding
 * copy is unified, so this type doesn't carry the discriminator.
 */
export interface RushFeeInfo {
  amount: string;
  hoursUntilShift: number;
}

export function formatRushFeeMessage(info: RushFeeInfo): string {
  const hoursText = info.hoursUntilShift === 1 ? "hour" : "hours";
  return `This shift starts in less than ${info.hoursUntilShift} ${hoursText}. A rush fee of ${info.amount} will be added to the shift rate as per your contract.`;
}

export function formatTierRushFeeMessage(tier: RushFeeTier): string {
  return formatRushFeeMessage({
    amount: formatChargeDifferentialPercent(tier.incrementalMultiplier),
    hoursUntilShift: tier.thresholdInHours,
  });
}

/**
 * Returns true when `shiftStart` falls inside the workplace's rush-fee window — i.e. the
 * shift is in the future but starts within `rushFeeConfig.period` hours, AND the workplace
 * has a non-zero `differential` configured.
 */
export function isWithinRushFeeWindow(
  rushFeeConfig: LegacyRushFee | undefined,
  shiftStart: Date
): boolean {
  if (!isDefined(rushFeeConfig) || rushFeeConfig.differential <= 0 || rushFeeConfig.period <= 0) {
    return false;
  }
  const hoursUntilShift = differenceInMinutes(shiftStart, getServerNow()) / 60;
  return hoursUntilShift > 0 && hoursUntilShift <= rushFeeConfig.period;
}

export function formatRushFeeAmount(differential: number): string {
  return `$${differential.toFixed(2)}/hour`;
}

/**
 * Returns the tier whose `thresholdInHours` is the smallest value still strictly greater
 * than the number of hours between now and `shiftStart`. Tiers are matched with `<`
 * (strict less than) so a shift exactly at the threshold falls into the next-larger band.
 *
 * Assumes `tiers` is sorted ascending by `thresholdInHours`. Returns `undefined` when no
 * tier matches (including when the shift is in the past or no tiers are configured).
 */
export function findApplicableRushFeeTier(
  tiers: RushFeeTier[],
  shiftStart: Date
): RushFeeTier | undefined {
  if (tiers.length === 0) {
    return undefined;
  }
  const hoursUntilShift = differenceInMinutes(shiftStart, getServerNow()) / 60;
  if (hoursUntilShift <= 0) {
    return undefined;
  }
  return tiers.find((tier) => hoursUntilShift < tier.thresholdInHours);
}

export type RushFeeResolution =
  | { source: "tier"; applicableTier: RushFeeTier }
  | { source: "legacy" };

/**
 * Single source of truth for rush-fee priority across every posting surface: try
 * the tiered schedule first (strict `<`), fall back to the legacy single-tier
 * window (`<=`) only when no tiers are configured. Returns `undefined` when no
 * rush fee applies.
 */
export function resolveRushFee(
  tiers: RushFeeTier[],
  rushFeeConfig: LegacyRushFee | undefined,
  shiftStart: Date
): RushFeeResolution | undefined {
  if (tiers.length > 0) {
    const applicableTier = findApplicableRushFeeTier(tiers, shiftStart);
    return applicableTier ? { source: "tier", applicableTier } : undefined;
  }
  return isWithinRushFeeWindow(rushFeeConfig, shiftStart) ? { source: "legacy" } : undefined;
}

export function isShiftShortNotice(
  tiers: RushFeeTier[],
  rushFeeConfig: LegacyRushFee | undefined,
  shiftStart: Date
): boolean {
  return isDefined(resolveRushFee(tiers, rushFeeConfig, shiftStart));
}

export function hasActiveLegacyRushFee(
  rushFeeConfig: LegacyRushFee | undefined
): rushFeeConfig is LegacyRushFee {
  return isDefined(rushFeeConfig) && rushFeeConfig.differential > 0 && rushFeeConfig.period > 0;
}
